import React, { createContext, useContext, useEffect, useState } from "react"

import { JSONObject, JSONSchema } from "@llumiverse/core"
import { VertesiaClient } from "@vertesia/client"
import { ExecutionEnvironmentRef, Interaction, mergePromptsSchema, PopulatedInteraction } from "@vertesia/common"

import { useUserSession } from "@session/UserSession"

export interface ConversationWorkflowPayload {
    interaction?: Interaction | undefined
    environment?: ExecutionEnvironmentRef | undefined
    model?: string
    data?: JSONObject | undefined
    tools: string[]
}

export class PayloadBuilder implements ConversationWorkflowPayload {
    _interactive: boolean = true
    payload: ConversationWorkflowPayload
    interactionParamsSchema?: JSONSchema | null

    constructor(
        public vertesia: VertesiaClient,
        public updateState: (data: PayloadBuilder) => void
    ) {
        this.payload = {
            model: "",
            tools: [],
        }
    }

    onStateChanged() {
        this.updateState(this.clone())
    }

    clone() {
        const builder = new PayloadBuilder(this.vertesia, this.updateState)
        builder.interactionParamsSchema = this.interactionParamsSchema
        builder.payload = this.payload
        builder._interactive = this._interactive
        return builder
    }

    get interactive() {
        return this._interactive
    }

    set interactive(interactive: boolean) {
        if (interactive !== this._interactive) {
            this._interactive = interactive
            this.onStateChanged()
        }
    }

    get interaction() {
        return this.payload.interaction
    }

    set interaction(interaction: Interaction | undefined) {
        if (interaction?.id !== this.payload.interaction?.id) {
            this.payload.interaction = interaction
            this.interactionParamsSchema = mergePromptsSchema(this.interaction as PopulatedInteraction) as JSONSchema
            if (interaction) {
                if (interaction.model) {
                    this.payload.model = interaction.model
                }
                if (interaction.environment) {
                    if (typeof interaction.environment === "string") {
                        this.vertesia.environments.retrieve(interaction.environment).then((environment) => (this.environment = environment))
                    } else {
                        this.payload.environment = interaction.environment
                    }
                }
            }
            this.onStateChanged()
        }
    }

    get environment() {
        return this.payload.environment
    }

    set environment(environment: ExecutionEnvironmentRef | undefined) {
        if (environment?.id !== this.payload.environment?.id) {
            this.payload.environment = environment
            this.onStateChanged()
        }
    }

    get model() {
        return this.payload.model
    }

    set model(model: string | undefined) {
        if (model !== this.payload.model) {
            this.payload.model = model
            this.onStateChanged()
        }
    }

    get tools() {
        return this.payload.tools
    }

    set tools(tools: string[]) {
        this.payload.tools = tools
        this.onStateChanged()
    }

    get data(): JSONObject | undefined {
        return this.payload.data
    }

    set data(data: JSONObject) {
        this.payload.data = data
        this.onStateChanged()
    }
}

export const PayloadContext = createContext<PayloadBuilder | undefined>(undefined)

interface PayloadProviderProps {
    children: React.ReactNode
}
export function PayloadBuilderProvider({ children }: PayloadProviderProps) {
    const { client } = useUserSession()
    const [builder, setBuilder] = useState<PayloadBuilder>()

    useEffect(() => {
        setBuilder(new PayloadBuilder(client, setBuilder))
    }, [client])

    return builder && <PayloadContext.Provider value={builder}>{children}</PayloadContext.Provider>
}

export function usePayloadBuilder() {
    const ctx = useContext(PayloadContext)
    if (!ctx) {
        throw new Error("usePayloadBuilder must be used within a PayloadProvider")
    }

    return ctx
}
